import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { LogOut, User2, LayoutDashboard, Sparkles } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col bg-[#F9F7F3] text-[#2C352D]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#F9F7F3]/80 border-b border-[#E8E2D6]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand-logo" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#0F766E] to-[#D97757] flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.5} />
            </div>
            <span className="font-serif-display text-2xl tracking-tight">Massage Savings</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            <NavLink to="/browse" data-testid="nav-browse" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-[#D97757]' : 'text-[#2C352D] hover:text-[#D97757]'}`}>Browse Deals</NavLink>
            <NavLink to="/need-therapist" data-testid="nav-need-therapist" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-[#0F766E]' : 'text-[#2C352D] hover:text-[#0F766E]'}`}>Need a Therapist?</NavLink>
            <NavLink to="/how-it-works" data-testid="nav-how" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-[#D97757]' : 'text-[#2C352D] hover:text-[#D97757]'}`}>How It Works</NavLink>
            <NavLink to="/faq" data-testid="nav-faq" className={({isActive}) => `text-sm font-medium transition-colors ${isActive ? 'text-[#D97757]' : 'text-[#2C352D] hover:text-[#D97757]'}`}>FAQ</NavLink>
          </nav>

          <div className="flex items-center gap-3">
            {user && user !== false ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button data-testid="user-menu-button" className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#E8E2D6] hover:bg-[#E8E2D6]/40 transition-colors">
                    <User2 className="w-4 h-4" strokeWidth={1.5} />
                    <span className="text-sm font-medium hidden sm:inline">{user.name?.split(' ')[0]}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="text-sm font-medium">{user.name}</div>
                    <div className="text-xs text-[#5F6C60]">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {(user.role === "individual" || user.role === "business") && (
                    <DropdownMenuItem data-testid="menu-dashboard" onClick={() => navigate("/dashboard")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Dashboard
                    </DropdownMenuItem>
                  )}
                  {user.role === "admin" && (
                    <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Admin
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                    <LogOut className="w-4 h-4 mr-2" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login" data-testid="nav-signin">
                  <Button variant="ghost" className="rounded-full">Sign in</Button>
                </Link>
                <Link to="/register" data-testid="nav-signup">
                  <Button className="rounded-full bg-[#D97757] hover:bg-[#C26243] text-white px-6">List a deal</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1">{children}</main>

      <footer className="border-t border-[#E8E2D6] mt-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-1 md:grid-cols-3 gap-10">
          <div>
            <div className="font-serif-display text-2xl mb-2">Massage Savings</div>
            <p className="text-sm text-[#5F6C60] max-w-sm leading-relaxed">
              A flat-fee marketplace where massage therapists keep 100% of their deals — and customers find the best local rates.
            </p>
            <p className="text-sm text-[#5F6C60] mt-3">
              <a href="mailto:hello@massagesavings.com" className="hover:text-[#D97757]">hello@massagesavings.com</a>
            </p>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] mb-3">Explore</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/browse" className="hover:text-[#D97757]">All deals</Link></li>
              <li><Link to="/for-therapists" className="hover:text-[#D97757]">For therapists</Link></li>
              <li><Link data-testid="footer-how" to="/how-it-works" className="hover:text-[#D97757]">How it works</Link></li>
              <li><Link data-testid="footer-faq" to="/faq" className="hover:text-[#D97757]">FAQ</Link></li>
              <li><Link data-testid="footer-contact" to="/contact" className="hover:text-[#D97757]">Contact</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#5F6C60] mb-3">Account</div>
            <ul className="space-y-2 text-sm">
              <li><Link to="/login" className="hover:text-[#D97757]">Sign in</Link></li>
              <li><Link to="/register" className="hover:text-[#D97757]">List a deal</Link></li>
              <li><Link data-testid="footer-terms" to="/terms" className="hover:text-[#D97757]">Terms of Service</Link></li>
              <li><Link data-testid="footer-privacy" to="/privacy" className="hover:text-[#D97757]">Privacy Policy</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[#E8E2D6] py-6 text-center text-xs text-[#5F6C60]">© {new Date().getFullYear()} Massage Savings. All rights reserved.</div>
      </footer>
    </div>
  );
}
